<template>
  <div class="w-full flex items-center justify-center gap-2">
    <MacroList
      :loading="repoLoading"
      :list="macroList"
      :selected-index="selectedListIndex"
      :highlighted-bind-index="highlightedBindIndex"
      :disabled="loadingCurrent || creatingMacro"
      :max-macro-count="maxMacroCount"
      :max-macro-byte-length="macroByteLengthLimit"
      @create="handleCreateMacro"
      @select="handleSelectMacro"
      @edit="handleRenameMacro"
      @delete="handleDeleteMacro"
      @bind-key="handleBindMacro"
    />

    <Operation
      :disabled="loadingCurrent || creatingMacro || (macroList.length > 0 && !currentMacroMeta)"
      :result="currentEditorData"
      :method="currentMethod"
      :max-macro-byte-length="macroByteLengthLimit"
      :enabled-wheels="enabledWheels"
      :coordinate-min="coordinateMin"
      :coordinate-max="coordinateMax"
      :ensure-ready="ensureMacroForRecording"
      @save="handleSaveMacro"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { toast } from '@rapoo/element'
  import { getMacroMaxLength, type ActionInfo, type MacroJson } from '@rapoo/device-sdk'
  import type { KeyConfig } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore } from '@/stores'
  import MacroList from './components/MacroList.vue'
  import Operation from './components/Operation/index.vue'
  import { useMacroRepository as useDefaultMacroRepository } from './composables/useMacroRepository'
  import {
    MOUSE_KEY_CODE,
    LOOP_MAX,
    MacroRepositorySymbol,
    type MacroEditorAction,
    type MacroEditorMethod,
    type MacroListItem,
    type UseMacroRepository,
    type WheelType,
  } from './types'
  import { useMacro } from './composables/useMacro'
  import {
    SelectedKeySymbol,
    KeySettingsSymbol,
    UpdateSelectedKeyMappingSymbol,
    RestoreKeyMappingWithTargetSymbol,
    type KeyChangePayload,
    type RestoreKeyMappingWithTarget,
    type UpdateSelectedKeyMapping,
  } from '../../types'
  import { findSettingByTargetKey } from '../../utils/keyIdentity'
  import { KEYCODE_KEYWORDS_MAP } from '../../utils/keyMapSettings'
  import {
    getEditorRecordEncodedByteLength,
    takeActionsWithinMacroByteLength,
  } from './utils/macroByteLength'

  const props = withDefaults(
    defineProps<{
      /** 最大宏数量，默认 128（键盘） */
      maxMacroCount?: number
      /** 单个宏内容最大字节数回退值；优先读取 manifest `macro.maxLength` */
      maxMacroByteLength?: number
      /** 是否禁用绑定流程的 toast 提示（鼠标侧默认选中按键，不需要提示） */
      disableBindToast?: boolean
      /**
       * 设备支持记录的滚轮方向。
       * - 空数组：不支持滚轮录制（键盘默认）
       * - ['vertical']：仅垂直滚轮
       * - ['horizontal']：仅水平滚轮
       * - ['vertical', 'horizontal']：两者皆可
       */
      enabledWheels?: WheelType[]
      /** 坐标动作允许的最小值，默认使用 12 位有符号范围。 */
      coordinateMin?: number
      /** 坐标动作允许的最大值，默认使用 12 位有符号范围。 */
      coordinateMax?: number
    }>(),
    {
      maxMacroCount: 128,
      maxMacroByteLength: 512,
      disableBindToast: false,
      enabledWheels: () => [],
      coordinateMin: -2048,
      coordinateMax: 2047,
    },
  )

  const { t } = useLocaleStore()
  const { currentDevice } = storeToRefs(useDeviceStore())
  const macroByteLengthLimit = computed(() =>
    getMacroMaxLength(currentDevice.value?.manifest.capabilities, props.maxMacroByteLength),
  )

  /* ------------------------------------------------------------------ */
  /*  ActionInfo ↔ MacroEditorAction 1:1 映射                           */
  /* ------------------------------------------------------------------ */

  const MOUSE_BUTTON_CODES = new Set<number>(Object.values(MOUSE_KEY_CODE))

  function infoToEditorAction(info: ActionInfo): MacroEditorAction {
    if (info.Type === 2) {
      return { type: 4, time: info.Delay }
    }
    if (info.Type === 3) {
      return { type: 2, x_coordinate: info.X ?? 0, y_coordinate: info.Y ?? 0 }
    }
    // 水平滚动
    if (info.Type === 4) {
      return { type: 3, vWheel: 0, hWheel: info.KeyCode }
    }
    // 垂直滚动
    if (info.Type === 5) {
      return { type: 3, vWheel: info.KeyCode, hWheel: 0 }
    }
    return { type: 1, keystroke: info.KeyCode, action: info.Action === 1 ? 1 : 0 }
  }

  function editorActionToInfoList(actions: MacroEditorAction[]): ActionInfo[] {
    const empty: ActionInfo = { Action: 0, Delay: 0, KeyCode: 0, Type: 0, X: 0, Y: 0 }
    return actions.map((action) => {
      if (action.type === 4) {
        return { ...empty, Type: 2, Delay: Math.max(0, Math.floor(action.time || 0)) }
      }
      if (action.type === 2) {
        return { ...empty, Type: 3, X: action.x_coordinate, Y: action.y_coordinate }
      }
      if (action.type === 3) {
        return {
          ...empty,
          Action: 0,
          KeyCode: action.hWheel !== 0 ? action.hWheel : action.vWheel,
          Type: action.hWheel !== 0 ? 4 : 5,
        }
      }
      const infoType = MOUSE_BUTTON_CODES.has(action.keystroke) ? 1 : 0
      return { ...empty, Action: action.action, KeyCode: action.keystroke, Type: infoType }
    })
  }

  function playTypeToMethod(
    playType: number,
    cycleNumber: number,
    ignoreDelay: boolean,
  ): MacroEditorMethod {
    if (playType === 2) return { index: 2, num: 1, ignoreTime: ignoreDelay }
    if (playType === 3) return { index: 3, num: 1, ignoreTime: ignoreDelay }
    return {
      index: 1,
      num: Math.min(LOOP_MAX, Math.max(cycleNumber || 1, 1)),
      ignoreTime: ignoreDelay,
    }
  }

  function methodToPlayType(method: MacroEditorMethod): {
    playType: number
    cycleNumber: number
  } {
    if (method.index === 2) return { playType: 2, cycleNumber: 0 }
    if (method.index === 3) return { playType: 3, cycleNumber: 0 }
    return {
      playType: 1,
      cycleNumber: Math.min(LOOP_MAX, Math.max(Math.floor(method.num || 1), 1)),
    }
  }

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)
  const restoreKeyMappingWithTarget = inject<RestoreKeyMappingWithTarget>(
    RestoreKeyMappingWithTargetSymbol,
  )

  // 允许外部（如鼠标侧）通过 provide 注入不同的宏仓储实现，未提供则使用键盘默认实现。
  const useMacroRepository = inject<UseMacroRepository>(
    MacroRepositorySymbol,
    () => useDefaultMacroRepository,
    true,
  )

  const {
    loading: repoLoading,
    macroList,
    loadMacroList,
    readMacroByIndex,
    saveMacroByIndex,
    createMacro,
    renameMacro,
    deleteMacro,
  } = useMacroRepository()

  const { macroToJson, jsonToMacro } = useMacro()

  // 当前选中的宏文件 index
  const selectedListIndex = ref(-1)
  const loadingCurrent = ref(false)
  /** 新增宏进行中，防止连点分配到同一 slot */
  const creatingMacro = ref(false)

  // 当前的列表项
  const currentMacroMeta = ref<MacroListItem | null>(null)
  // 当前操作的编辑数据
  const currentEditorData = ref<MacroEditorAction[]>([])
  // 编辑器中的播放方法
  const currentMethod = ref<MacroEditorMethod>({
    num: 1,
    index: 1,
    ignoreTime: false,
  })

  // 绑定流程：待绑定的宏项（Flow 1: 先点绑定按钮，再选键）
  const pendingBindItem = ref<MacroListItem | null>(null)
  const highlightedBindIndex = ref<number | null>(null)

  /** 避免新建宏时与 length watcher 并发重复 select */
  let selectingMacroPromise: Promise<void> | null = null
  let selectingMacroListIndex = -1

  const getNextMacroSlotIndex = () => {
    const used = new Set(macroList.value.map((item) => item.index))
    for (let index = 0; index < props.maxMacroCount; index++) {
      if (!used.has(index)) return index
    }
    return -1
  }

  // 选择当前的宏文件
  const selectMacroByListIndex = async (listIndex: number) => {
    // 非法index，清空当前状态
    if (listIndex < 0 || listIndex >= macroList.value.length) {
      selectedListIndex.value = -1
      currentMacroMeta.value = null
      currentEditorData.value = []
      currentMethod.value = { num: 1, index: 1, ignoreTime: false }
      return
    }

    // 同一项正在加载时复用 in-flight promise，避免空列表录制新建时双重点击/watcher 竞态
    if (selectingMacroPromise && selectingMacroListIndex === listIndex) {
      await selectingMacroPromise
      return
    }

    const target = macroList.value[listIndex]
    if (!target) return

    selectingMacroListIndex = listIndex
    // 先占住选中索引，让 length watcher 不会再开一次 select
    selectedListIndex.value = listIndex
    loadingCurrent.value = true

    selectingMacroPromise = (async () => {
      try {
        const macro = await readMacroByIndex(target.index)
        console.log(macro)
        // 选中已切换则丢弃过期结果
        if (selectedListIndex.value !== listIndex) return

        const macroJson = macroToJson(macro)
        const m = macroJson.Macro

        currentMacroMeta.value = {
          name: target.name,
          index: target.index,
        }
        currentEditorData.value = m.InfoList.map(infoToEditorAction)
        currentMethod.value = playTypeToMethod(m.PlayType, m.CycleNumber, m.IgnoreDelay)
      } finally {
        if (selectingMacroListIndex === listIndex) {
          loadingCurrent.value = false
          selectingMacroPromise = null
          selectingMacroListIndex = -1
        }
      }
    })()

    await selectingMacroPromise
  }

  const handleSelectMacro = async (listIndex: number) => {
    if (listIndex === selectedListIndex.value) return
    await selectMacroByListIndex(listIndex)
  }

  const handleCreateMacro = async (): Promise<boolean> => {
    if (creatingMacro.value) return false
    creatingMacro.value = true
    try {
      const nextIndex = getNextMacroSlotIndex()
      if (nextIndex < 0) {
        toast.warning(t('toast.keys.macroMax', { count: props.maxMacroCount }))
        return false
      }

      const newItem: MacroListItem = {
        index: nextIndex,
        // 按当前列表序号命名（第 N 个宏 → MacroN），与 slot index 解耦
        name: `Macro${macroList.value.length + 1}`,
        hasContent: false,
      }

      // 先写入 SDK，再更新列表，确保 watcher 触发 selectMacroByListIndex 时宏已存在
      await createMacro(newItem)
      macroList.value = [...macroList.value, newItem]
      return true
    } finally {
      creatingMacro.value = false
    }
  }

  /**
   * 开始录制前确保有可写入的宏：
   * - 已有选中宏：直接开始
   * - 空列表：先新建并选中（与「新建」一致）
   */
  const ensureMacroForRecording = async (): Promise<boolean> => {
    if (currentMacroMeta.value) return true

    if (macroList.value.length > 0) {
      if (selectedListIndex.value < 0) {
        await selectMacroByListIndex(0)
      }
      return !!currentMacroMeta.value
    }

    const created = await handleCreateMacro()
    if (!created || macroList.value.length === 0) return false

    await selectMacroByListIndex(macroList.value.length - 1)
    return !!currentMacroMeta.value
  }

  const handleRenameMacro = async ({
    item,
    name,
    listIndex,
  }: {
    item: MacroListItem
    name: string
    listIndex: number
  }) => {
    // 先同步更新 UI，避免 input 消失后显示旧名称再闪到新名称
    macroList.value = macroList.value.map((entry, index) => {
      if (index !== listIndex) return entry
      return {
        ...entry,
        name,
      }
    })

    if (currentMacroMeta.value?.index === item.index) {
      currentMacroMeta.value = {
        ...currentMacroMeta.value,
        name,
      }
    }

    // 同步更新 keySettings 中已绑定该宏的按键的 macroName，使 tooltip 实时显示新名称
    if (keySettings?.value) {
      let changed = false
      for (const setting of keySettings.value) {
        if (setting.keyType === 'macro' && setting.raw?.macro?.index === item.index) {
          setting.raw.macroName = name
          changed = true
        }
      }
      if (changed) {
        keySettings.value = [...keySettings.value]
      }
    }

    // 异步写入设备
    await renameMacro({
      index: item.index,
      name,
    })
  }

  /** 清除所有绑定到指定宏 slot 的按键（先恢复键位，再删宏） */
  const clearMacroKeyBindings = (macroIndex: number) => {
    if (!keySettings?.value || !restoreKeyMappingWithTarget) return

    const boundKeywords = keySettings.value
      .filter((setting) => setting.keyType === 'macro' && setting.raw?.macro?.index === macroIndex)
      .map((setting) => setting.keywords)

    for (const keywords of boundKeywords) {
      const config = KEYCODE_KEYWORDS_MAP.get(keywords)
      const targetKey = (config ? { ...config } : { keywords }) as KeyConfig
      restoreKeyMappingWithTarget(targetKey)
    }
  }

  const handleDeleteMacro = async ({
    item,
    listIndex,
  }: {
    item: MacroListItem
    listIndex: number
  }) => {
    // 删除的是待绑定项，取消 pending 状态
    if (pendingBindItem.value?.index === item.index) {
      cancelPendingBind()
      toast.dismiss('macro-bind-select-key')
    }

    // 先清除已绑定该宏的按键，再删除宏内容
    clearMacroKeyBindings(item.index)

    macroList.value = macroList.value.filter((_, index) => index !== listIndex)

    await deleteMacro(item.index)

    if (!macroList.value.length) {
      await selectMacroByListIndex(-1)
      return
    }

    // 删除当前选中项时，默认回到第一项。
    if (selectedListIndex.value === listIndex) {
      await selectMacroByListIndex(0)
      return
    }

    // 删除的是选中项之前的项，需要修正当前选中索引。
    if (selectedListIndex.value > listIndex) {
      selectedListIndex.value -= 1
    }
  }

  const handleSaveMacro = async (payload: {
    data: MacroEditorAction[]
    method: MacroEditorMethod
  }) => {
    if (!currentMacroMeta.value) return
    const fitted = takeActionsWithinMacroByteLength(
      payload.data,
      macroByteLengthLimit.value,
      getEditorRecordEncodedByteLength,
    )
    const editorData = fitted.actions
    const { playType, cycleNumber } = methodToPlayType(payload.method)
    const macroJson: MacroJson = {
      Macro: {
        CycleNumber: cycleNumber,
        ID: currentMacroMeta.value.index,
        IgnoreDelay: payload.method.ignoreTime,
        InfoList: editorActionToInfoList(editorData),
        Name: currentMacroMeta.value.name,
        PlayType: playType,
      },
      Platform: 'AHub',
    }
    const sdkMacro = jsonToMacro(macroJson)

    await saveMacroByIndex(sdkMacro, currentMacroMeta.value.index)

    currentEditorData.value = editorData
    currentMethod.value = payload.method

    // 更新 hasContent
    const savedListIndex = macroList.value.findIndex(
      (m) => m.index === currentMacroMeta.value!.index,
    )
    if (savedListIndex >= 0) {
      macroList.value[savedListIndex] = {
        ...macroList.value[savedListIndex],
        hasContent: editorData.length > 0,
      }
    }

    if (fitted.truncated) {
      toast.warning(t('toast.keys.macroRecordingLimit2'))
    }
    toast.success(t('toast.keys.macroSaved'))
  }

  const cancelPendingBind = () => {
    pendingBindItem.value = null
    highlightedBindIndex.value = null
  }

  // 实际宏绑定按键操作
  const executeBinding = async (item: MacroListItem) => {
    if (!selectedKey?.value) return
    if (selectedKey.value.keywords === '上滚' || selectedKey.value.keywords === '下滚') {
      toast.warning(t('toast.keys.wheelRestricted2'))
      return
    }
    if (!updateSelectedKeyMapping) {
      console.error('当前页面未注入改键能力')
      return
    }

    const detail = await readMacroByIndex(item.index)
    const loop = detail.loop ?? 1

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      // keyLabel: t('keys.macro.label'),
      keyType: 'macro',
      target: null,
      raw: {
        type: 0x08,
        macroName: item.name,
        macro: {
          index: item.index,
          loop,
        },
      },
    }

    updateSelectedKeyMapping(payload)
    // 键盘：绑定后清空选中，便于「先点宏再选键」流程。
    // 鼠标（disableBindToast）：始终保持按键选中，清空会导致 UI 仍高亮但改键静默失败。
    if (!props.disableBindToast) {
      selectedKey.value = null
    }
    cancelPendingBind()
    toast.dismiss('macro-bind-select-key')
    toast.dismiss('macro-bind-select-macro')
  }

  const handleBindMacro = async (payload: { item: MacroListItem; index: number }) => {
    const { item, index } = payload

    // 虚拟键盘已选中按键 → 直接绑定
    if (selectedKey?.value) {
      await executeBinding(item)
      return
    }

    // 未选中按键 → 进入待绑定模式
    // 再次点击同一个绑定按钮 → 取消
    if (pendingBindItem.value?.index === item.index && highlightedBindIndex.value === index) {
      cancelPendingBind()
      toast.dismiss('macro-bind-select-key')
      return
    }

    pendingBindItem.value = item
    highlightedBindIndex.value = index
    if (!props.disableBindToast) {
      toast.info(t('toast.keys.macroBindSelectKey'), {
        id: 'macro-bind-select-key',
        duration: Infinity,
      })
    }
  }

  onMounted(() => {
    // 始终从设备重拉列表，避免恢复出厂后沿用模块级单例里的旧宏名
    // （旧逻辑在 macroList 非空时会跳过加载，直接读详情，易读到擦除后的 0xFFFF 长度）
    if (!repoLoading.value) {
      loadMacroList()
    }
  })

  // 流式加载过程中，第一条数据到达时自动选中第一项
  watch(
    () => macroList.value.length,
    async (len) => {
      if (len > 0 && selectedListIndex.value < 0) {
        await selectMacroByListIndex(0)
      }
    },
    { deep: true },
  )

  // 当键盘上选中了一个已绑定宏的按键时，自动在列表中选中对应宏并回显内容
  watch(
    () => selectedKey?.value,
    async (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'macro' || setting.raw?.macro?.index == null) return

      const macroIndex = setting.raw.macro.index
      const listIndex = macroList.value.findIndex((item) => item.index === macroIndex)
      if (listIndex < 0) return

      // 避免重复加载同一个宏
      if (selectedListIndex.value === listIndex) return

      await selectMacroByListIndex(listIndex)
    },
  )

  // 绑定流程：监听虚拟键盘选中状态变化
  watch(
    () => selectedKey?.value,
    async (key) => {
      if (!key) {
        // 按键取消选中（如右键取消）→ 清除 pending 状态
        if (pendingBindItem.value) {
          cancelPendingBind()
        }
        return
      }

      // 有待绑定宏 + 按键刚被选中 → 自动执行绑定
      if (pendingBindItem.value) {
        await executeBinding(pendingBindItem.value)
        return
      }

      // 按键已选中，无待绑定宏 → 提示选择宏文件（id 与 TargetKeyboardPanel 中保持一致）
      if (!props.disableBindToast) {
        toast.info(t('toast.keys.macroBindSelectMacro'), {
          id: 'macro-bind-select-macro',
          duration: Infinity,
        })
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    // 仅清理本 tab 内的待绑定状态，不清除虚拟键盘选中（由路由级 index.vue 统一管理）
    cancelPendingBind()
  })
</script>

<style scoped></style>
